import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import FutureBoardProgress from "components/futureBoard/FutureBoardProgress";
import Snackbar from "@mui/material/Snackbar";
import DashboardLayout from "parts/LayoutContainers/DashboardLayout";
import DashboardNavbar from "parts/Navbars/DashboardNavbar";
import Footer from "parts/Footer";
import MDBox from "components/MDBox";
import FutureBoardTheme from "components/futureBoard/FutureBoardTheme";
import { LanguageContext } from "context";
import { useSessionContext } from "context/SessionContext";
import {
  changeFutureCardStatus, createFutureCard, deleteFutureCard, isRetryable, listFutureCards, parseFutureBoardError,
  reorderFutureCards, setPinnedFutureCard, buildCreateInput, EMPTY_DRAFT, updateDraft,
  type FeelingSupport, type FutureBoardFailure, type FutureCardList, type FutureCardStatus, type FutureCardSummary,
} from "@chronospark/shared";
import BoardList from "components/futureBoard/BoardList";
import { CreateCardDialog, DeleteCardDialog } from "components/futureBoard/CardDialogs";
import FailureAlert from "components/futureBoard/FailureAlert";
import FutureCardEditor from "components/futureBoard/FutureCardEditor";
import OneScene from "components/futureBoard/OneScene";
import { usePaywall } from "context/PaywallContext";
import type { TileAction } from "components/futureBoard/CardTile";

type Translate = (key: string, options?: object) => string;

const STATUS_FOR_ACTION: Partial<Record<TileAction, FutureCardStatus>> = {
  show: "visible", restore: "visible", toDraft: "draft", archive: "archived",
};

function FutureBoard() {
  const { accessToken, userInfo } = useSessionContext() as { accessToken?: string; userInfo?: { plan_type?: string } | null };
  const { openPaywall } = usePaywall();
  const { t, language } = useContext(LanguageContext) as { t: Translate; language: string };
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const sceneParam = Number(params.get("card"));
  const sceneId = Number.isInteger(sceneParam) && sceneParam > 0 ? sceneParam : null;
  const editing = sceneId !== null && params.get("edit") === "1";
  const editStep = Number(params.get("step")) || 0;
  // 画面の案内用（自分の写真は Pro のみ）。最終的な判断はサーバーが行う。
  const isPro = userInfo?.plan_type === "Pro";

  const [list, setList] = useState<FutureCardList | null>(null);
  const [status, setStatus] = useState<FutureCardStatus>("visible");
  const [failure, setFailure] = useState<FutureBoardFailure | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState<FutureCardSummary | null>(null);
  // 作成の再送で重複させないための ID。通信の失敗で再試行するときだけ同じ値を使う。
  const createRequest = useRef<{ title: string; id: string } | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!accessToken) return;
    try {
      setList(await listFutureCards(accessToken, undefined, { hideGlobalLoader: quiet }));
      // 静かな取り直し（競合のあとなど）では、利用者に見せている失敗の表示を消さない
      if (!quiet) setFailure(null);
    } catch (error) {
      if (!quiet) setFailure(parseFutureBoardError(error));
    }
  }, [accessToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const fail = useCallback((error: unknown) => {
    const parsed = parseFutureBoardError(error);
    setFailure(parsed);
    // 競合や、対象がなくなった場合は、最新の状態を静かに取り直す（自動で上書きはしない）
    if (parsed.kind === "conflict" || parsed.kind === "board_conflict" || parsed.kind === "not_found" || parsed.kind === "rejected") void load(true);
    return parsed;
  }, [load]);

  const openScene = (card: { id: number }) => setParams({ card: String(card.id) });
  const closeScene = () => {
    setParams({});
    void load(true);
  };
  const openEditor = (id: number, step = 0) => setParams({ card: String(id), edit: "1", ...(step ? { step: String(step) } : {}) });
  const closeEditor = () => {
    if (sceneId !== null) setParams({ card: String(sceneId) });
  };
  // 気持ちの確認で選んだ提案。押したときだけ、編集画面などへ進む。
  const onSupport = (support: FeelingSupport) => {
    if (sceneId === null) return;
    if (support === "describe_role") openEditor(sceneId, 1);
    else if (support === "explore_wish" || support === "edit_text") openEditor(sceneId, 0);
  };

  const run = async (work: () => Promise<unknown>, successMessage?: string) => {
    setFailure(null);
    try {
      await work();
      await load(true);
      if (successMessage) setNotice(successMessage);
    } catch (error) {
      fail(error);
      throw error;
    }
  };

  const onAction = (card: FutureCardSummary, action: TileAction) => {
    if (!accessToken) return;
    if (action === "delete") {
      setDeleting(card);
      return;
    }
    if (action === "pin" || action === "unpin") {
      void run(() => setPinnedFutureCard(accessToken, action === "pin" ? card.id : null), t(action === "pin" ? "futureBoard.list.pinned" : "futureBoard.list.unpinned")).catch(() => undefined);
      return;
    }
    const next = STATUS_FOR_ACTION[action];
    if (next) void run(() => changeFutureCardStatus(accessToken, card.id, next, card.version), t("futureBoard.list.statusChanged")).catch(() => undefined);
  };

  const confirmDelete = async () => {
    if (!accessToken || !deleting) return;
    setBusy(true);
    try {
      await run(() => deleteFutureCard(accessToken, deleting.id), t("futureBoard.delete.done"));
      setDeleting(null);
    } catch {
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  };

  const create = async (title: string) => {
    if (!accessToken) return;
    const draft = updateDraft(EMPTY_DRAFT, { title });
    const reuse = createRequest.current?.title === title.trim() ? createRequest.current.id : null;
    const id = reuse ?? crypto.randomUUID();
    createRequest.current = { title: title.trim(), id };
    setBusy(true);
    setFailure(null);
    try {
      const { card } = await createFutureCard(accessToken, buildCreateInput(draft, id));
      createRequest.current = null;
      setCreateOpen(false);
      setNotice(t("futureBoard.list.created"));
      await load(true);
      openEditor(card.id);
    } catch (error) {
      const parsed = fail(error);
      // 再送できない失敗では、次は新しい ID で作る
      if (!isRetryable(parsed)) createRequest.current = null;
      setCreateOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const reorder = async (ids: number[]) => {
    if (!accessToken || !list) return;
    const result = await reorderFutureCards(accessToken, ids, list.board_version).catch(error => {
      fail(error);
      throw error;
    });
    setList({ ...list, board_version: result.board_version });
    await load(true);
    setNotice(t("futureBoard.list.orderSaved"));
  };

  const unavailable = failure?.kind === "disabled";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <FutureBoardTheme>
        <MDBox py={{ xs: 2, md: 3 }}>
          <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: 0, sm: 1 } }}>
            {unavailable ? (
              <Alert severity="info">
                <strong>{t("futureBoard.unavailable.title")}</strong>
                <br />
                {t("futureBoard.unavailable.body")}
              </Alert>
            ) : (
              <>
                {failure && sceneId === null && <FailureAlert failure={failure} t={t} onReload={() => void load()} onClose={() => setFailure(null)} />}
                {editing && sceneId !== null ? (
                  <FutureCardEditor
                    cardId={sceneId}
                    accessToken={accessToken}
                    language={language}
                    t={t}
                    isPro={isPro}
                    startStep={editStep}
                    onRequirePro={openPaywall}
                    onDone={closeEditor}
                    onFailure={setFailure}
                    onNotice={setNotice}
                  />
                ) : sceneId !== null ? (
                  <OneScene
                    cardId={sceneId}
                    accessToken={accessToken}
                    language={language}
                    t={t}
                    onBack={closeScene}
                    onEdit={() => openEditor(sceneId)}
                    onFailure={setFailure}
                    onNotice={setNotice}
                    onSupport={onSupport}
                    onPlanToday={() => navigate(`/pages/task/DailyCoach?future_card_id=${sceneId}`)}
                  />
                ) : !list ? (
                  !failure && <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><FutureBoardProgress aria-label={t("Common.loading")} /></Box>
                ) : (
                  <BoardList
                    list={list}
                    language={language}
                    t={t}
                    status={status}
                    onStatusChange={setStatus}
                    onOpen={openScene}
                    onAction={onAction}
                    onCreate={() => setCreateOpen(true)}
                    onReorder={reorder}
                  />
                )}
              </>
            )}
          </Box>
        </MDBox>

        <CreateCardDialog open={createOpen} busy={busy} t={t} onCancel={() => setCreateOpen(false)} onCreate={create} />
        <DeleteCardDialog open={Boolean(deleting)} title={deleting?.title ?? ""} busy={busy} t={t} onCancel={() => setDeleting(null)} onConfirm={confirmDelete} />
        <Snackbar open={Boolean(notice)} autoHideDuration={4000} onClose={() => setNotice(null)} message={notice ?? ""} />
      </FutureBoardTheme>
      <Footer />
    </DashboardLayout>
  );
}

export default FutureBoard;
